import {
  AlertTriangleIcon,
  CheckCircleIcon,
  IconSizes,
  InfoCircleIcon,
} from '@newcore/icons';
import { Card } from '@newcore/ui/card';
import { cn } from '@newcore/ui/cn';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Text } from '@newcore/ui/typography';
import {
  type DiagnoseMcpServerResponse,
  McpServerLookupResult,
} from '@/api/user-portal-api-generated';
import { countWarnings, isInconclusive } from '../utils/verdict';

interface VerdictCardProps {
  icon: typeof CheckCircleIcon;
  cardClassName: string;
  iconClassName: string;
  headline: string;
  detail: string;
  serverUrl: string;
}

function VerdictCard({
  icon: Icon,
  cardClassName,
  iconClassName,
  headline,
  detail,
  serverUrl,
}: VerdictCardProps) {
  return (
    <Card className={cn('flex gap-3 p-4', cardClassName)}>
      <Icon
        size={IconSizes.LG}
        className={cn('mt-0.5 shrink-0', iconClassName)}
        aria-hidden='true'
      />
      <div className='flex min-w-0 flex-col gap-1'>
        <Text
          as='p'
          size={TextSizes.LG}
          weight={FontWeights.SEMIBOLD}
          className='text-primary'
          data-testid='mcp-tester-verdict'
        >
          {headline}
        </Text>
        <Text as='p' className='text-secondary'>
          {detail}
        </Text>
        <Text
          as='p'
          size={TextSizes.XS}
          className='text-tertiary break-all pt-1 font-mono'
        >
          {serverUrl}
        </Text>
      </div>
    </Card>
  );
}

interface ServerVerdictProps {
  serverUrl: string;
}

interface UsableVerdictProps extends ServerVerdictProps {
  warnings: number;
}

function UsableVerdict({ serverUrl, warnings }: UsableVerdictProps) {
  const fixes = warnings === 1 ? 'One thing is' : `${warnings} things are`;
  const detail =
    warnings === 0
      ? 'NewCore can connect to this server, and it published everything we look for.'
      : `NewCore can connect to this server. ${fixes} worth fixing first.`;

  return (
    <VerdictCard
      icon={CheckCircleIcon}
      cardClassName='border-utility-green-200 bg-utility-green-50'
      iconClassName='text-utility-green-500'
      headline='Usable as a custom MCP server'
      detail={detail}
      serverUrl={serverUrl}
    />
  );
}

function InconclusiveVerdict({ serverUrl }: ServerVerdictProps) {
  return (
    <VerdictCard
      icon={InfoCircleIcon}
      cardClassName='border-secondary bg-secondary'
      iconClassName='text-tertiary'
      headline='Could not finish the check'
      detail='This says nothing about the server. Try again in a moment.'
      serverUrl={serverUrl}
    />
  );
}

function NeedsWorkVerdict({ serverUrl }: ServerVerdictProps) {
  return (
    <VerdictCard
      icon={AlertTriangleIcon}
      cardClassName='border-utility-amber-200 bg-utility-amber-50'
      iconClassName='text-utility-amber-500'
      headline='Not usable yet'
      detail='The check stopped at the step below. Fixing it is what unblocks this server.'
      serverUrl={serverUrl}
    />
  );
}

interface VerdictProps {
  result: DiagnoseMcpServerResponse;
  serverUrl: string;
}

export function Verdict({ result, serverUrl }: VerdictProps) {
  if (result.status === McpServerLookupResult.OK) {
    return (
      <UsableVerdict
        serverUrl={serverUrl}
        warnings={countWarnings(result.steps ?? [])}
      />
    );
  }

  if (isInconclusive(result.failure)) {
    return <InconclusiveVerdict serverUrl={serverUrl} />;
  }

  return <NeedsWorkVerdict serverUrl={serverUrl} />;
}
